<template>
  <div class="pb-4 relative">
    <div
      v-if="perm().projects != 'modification'"
      style="position:absolute;top:0;left:0;width:100%;height:100%;z-index:1000;cursor:not-allowed;background:rgba(255,255,255,0.01);"
      @mousedown.stop
      @mouseup.stop
      @click.stop
      @pointerdown.stop
      @pointerup.stop
      @touchstart.stop
      @touchend.stop
    ></div>

    <div class="flex items-center justify-end mb-2">
      <button
        class="callToAction !px-4"
        @click="editSectionName=''; popupSectionVisible = true"
      >
        Ajouter une section
      </button>
    </div>

    <!-- Container avec scroll vertical -->
    <div class="overflow-y-auto rounded-lg">
      <table class="min-w-full border border-gray-200 rounded-lg bg-white">
        <thead class="bg-secondary text-white font-bold text-left sticky top-0">
          <tr>
            <th class="px-4 py-2 border-b w-5/20">Action</th>
            <th class="px-4 py-2 border-b w-3/20">Responsable</th>
            <th class="px-4 py-2 border-b w-2/20">Temps d'accès</th>
            <th class="px-4 py-2 border-b w-3/20">Dates</th>
            <th class="px-4 py-2 border-b w-4/20">Ressources</th>
            <th class="px-4 py-2 border-b w-3/20">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groupedEvents" :key="group.key">
            <!-- Ligne de séparateur du mois -->
            <tr @click="toggleGroup(group.key)" class="cursor-pointer bg-gray-300 select-none">
              <td colspan="7" class="px-4 py-2">
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <!-- Flèche à gauche -->
                    <span class="mr-2">
                      <i :class="{'fas fa-chevron-up': isGroupOpen(group.key), 'fas fa-chevron-down': !isGroupOpen(group.key)}"></i>
                    </span>
                    <span class="font-semibold">
                      {{ group.section }}
                    </span>
                    <!-- Icône crayon à droite du nom, sauf pour "Sans section" -->
                    <span
                      v-if="group.key !== 'Sans section'"
                      class="ml-2 align-middle"
                      @click.stop="openEditSection(group.section)"
                    >
                      <i class="fas fa-pen text-xs cursor-pointer text-gray-500 hover:text-primary"></i>
                    </span>
                    <span v-if="group.key !== 'Sans section'" class="ml-2 flex gap-1">
                      <button
                        class="text-gray-500 hover:text-primary"
                        @click.stop="moveSectionUp(group.section)"
                        :disabled="project.sections.indexOf(group.section) === 0"
                        title="Monter"
                      >
                        <i class="fas fa-arrow-up"></i>
                      </button>
                      <button
                        class="text-gray-500 hover:text-primary"
                        @click.stop="moveSectionDown(group.section)"
                        :disabled="project.sections.indexOf(group.section) === project.sections.length - 1"
                        title="Descendre"
                      >
                        <i class="fas fa-arrow-down"></i>
                      </button>
                    </span>
                  </div>
                  <div class="w-fit" v-if="group.section !== 'Sans section'">
                    <button class="!w-9 !h-9 !text-xl callToAction" @click.stop="handleAddEvent(group.section)">
                      +
                    </button>
                  </div>
                </div>
              </td>
            </tr>
            <!-- Lignes de paiements pour ce mois -->
            <template v-if="isGroupOpen(group.key)">
              <tr 
                v-for="event in group.events" 
                :key="event.id" 
                class="hover:bg-red-50 even:bg-red-50 odd:bg-white select-none"
              >
                <td class="px-4 py-2 border-b border-gray-300 text-left text-gray-700 whitespace-nowrap">
                  {{ event.title }}
                </td>
                <td class="px-4 py-2 border-b border-gray-300 text-left text-gray-700 whitespace-nowrap">
                  Non disponible
                </td>
                <td class="px-4 py-2 border-b border-gray-300 text-left text-gray-700 whitespace-nowrap">
                  {{ event.accessTime }}
                </td>
                <td class="px-4 py-2 border-b border-gray-300 text-left text-gray-700 whitespace-nowrap">
                  Du {{ formatDate(event.startDate) }} <br>
                  Au {{ formatDate(event.endDate) }} <br>
                  Durée : <span class="font-bold">{{ formatDuration(event.startDate, event.endDate) }}</span>
                </td>
                <td
                  class="px-4 py-2 border-b border-gray-300 text-left text-gray-700 whitespace-nowrap truncate max-w-[120px]"
                  :title="getRessourceNames(event)"
                >
                  <template v-for="(name, idx) in getRessourceNamesArray(event).slice(0, 2)" :key="idx">
                    <span class="block truncate">{{ name }}</span>
                  </template>
                  <span v-if="getRessourceNamesArray(event).length > 2" class="block text-gray-400">...</span>
                </td>
                <td class="px-4 py-2 border-b border-gray-300 text-left text-gray-700 whitespace-nowrap">
                  <div class="flex items-center w-full">
                    <button class="callToAction w-full" @click="popupEvent = event; popupEventVisible = true">
                      Modifier
                    </button>
                    <button
                      class="text-gray-500 hover:text-primary !m-0 !mr-1 !ml-2"
                      @click.stop="moveEventUp(event, group.events, group.section)"
                      @dblclick.stop
                      :disabled="group.events.indexOf(event) === 0"
                      title="Monter"
                    >
                      <i class="fas fa-arrow-up"></i>
                    </button>
                    <button
                      class="text-gray-500 hover:text-primary !m-0"
                      @click.stop="moveEventDown(event, group.events, group.section)"
                      @dblclick.stop
                      :disabled="group.events.indexOf(event) === group.events.length - 1"
                      title="Descendre"
                    >
                      <i class="fas fa-arrow-down"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>

    <PopupSection
      :visible="popupSectionVisible"
      :section="editSectionName || ''"
      @close="popupSectionVisible = false"
      @add="addSection($event)"
      @update="updateSection($event)"
      @delete="deleteSection($event)"
    />

    <PopupEvent
      :event="popupEvent"
      :visible="popupEventVisible" 
      :byIdea="false"
      :project="project"
      @close="popupEventVisible = false"
      @delete="handleDeleteEvent($event)"
    />
  </div>
</template>

<script>
import { provide, reactive, inject } from 'vue';
import { fetchEventsByProjectId } from '@/services/eventService';
import { fetchRessources } from '@/services/ressourceService';
import { formatDate } from 'date-fns';

import { updateEvent, deleteEvent } from '@/services/eventService';

import PopupSection from '@/components/Popups/PopupSection.vue';
import PopupEvent from '@/components/Popups/PopupEvent.vue';

export default {
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  components: {
    PopupSection,
    PopupEvent,
  },
  data() {
    return {
      // openGroups stocke l'état d'ouverture (true / false) de chaque groupe identifié par une clé (par ex. "2025-4")
      openGroups: {},
      ressources: [],
      popupSectionVisible: false,
      editSectionName: '',
      popupEventVisible: false,
      popupEvent: null,
      emptyEvent: {
        ressources: [],
        startDate: new Date(),
        endDate: new Date(),
        accessTime: "00:00",
        title: "",
        description: "",
        projectId: this.project ? this.project.id : "",
        clientId: this.project ? this.project.clientId : "",
        isFinished: false,
        color: "#E8535D",
        tasks: [],
        reminders: [],
        projectSection: "",
        order: 0,
      }
    };
  },
  computed: {
    groupedEvents() {
      console.log("Grouping events for project:");
      // 1. Prépare la liste des sections dans l'ordre du projet
      const sectionOrder = Array.isArray(this.project.sections) ? this.project.sections : [];
      const groups = {};

      // 2. Ajoute toutes les sections du projet, même vides
      sectionOrder.forEach(section => {
        groups[section] = [];
      });

      // 3. Ajoute le groupe "Sans section" si besoin
      groups["Sans section"] = [];

      // 4. Répartit les événements dans les bons groupes
      this.events.forEach(event => {
        if (event.noEdit) return;
        const section =
          event.projectSection &&
          event.projectSection.trim() &&
          sectionOrder.includes(event.projectSection)
            ? event.projectSection
            : "Sans section";
        groups[section].push(event);
      });

      // 5. Construit le résultat dans l'ordre voulu
      const result = [];

      // "Sans section" toujours en premier s'il y a des events dedans
      if (groups["Sans section"].length > 0) {
        result.push({
          key: "Sans section",
          section: "Sans section",
          events: groups["Sans section"].slice().sort((a, b) => new Date(a.date) - new Date(b.date))
        });
      }

      // Puis toutes les sections du projet, même vides
      sectionOrder.forEach(section => {
        result.push({
          key: section,
          section,
          events: groups[section].slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        });
      });

      return result;
    }
  },
  methods: {
    toggleGroup(key) {
      // Bascule l'état pour ce groupe
      this.openGroups[key] = !this.isGroupOpen(key);
    },
    isGroupOpen(key) {
      // Par défaut, le groupe est ouvert (true)
      if (this.openGroups[key] === undefined) {
        return true;
      }
      return this.openGroups[key];
    },
    formatDate(date) {
      return formatDate(new Date(date), 'dd/MM/yyyy HH:mm');
    },
    formatDuration(startDate, endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffMs = end - start;
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays >= 1) {
        const roundedDays = Math.round(diffDays);
        return `${roundedDays} jour${roundedDays >= 2 ? 's' : ''}`;
      }
      const diffHours = diffMs / (1000 * 60 * 60);
      const roundedHours = Math.round(diffHours);
      return `${roundedHours} heure${roundedHours >= 2 ? 's' : ''}`;
    },
    getRessourceNamesArray(event) {
      if (!event.ressources || !Array.isArray(event.ressources)) return [];
      return event.ressources
        .map(er => {
          const res = this.ressources.find(r => r.id === (er.ressourceId || er.id));
          if (res) return `${res.firstName} ${res.lastName}`;
          return '';
        })
        .filter(Boolean);
    },
    getRessourceNames(event) {
      return this.getRessourceNamesArray(event).join('\n');
    },
    addSection(sectionName) {
      if(!sectionName || sectionName.trim() === '') {
        return;
      }

      if (!this.project.sections) {
        this.project.sections = [];
      }
      this.project.sections.push(sectionName);
    },
    updateSection(sectionName) {
      if(!sectionName || sectionName.trim() === '') {
        return;
      }

      const oldSectionName = this.editSectionName;
      const index = this.project.sections.indexOf(oldSectionName);
      if (index !== -1) {
        this.project.sections[index] = sectionName;
      }

      // Met à jour tous les events qui avaient l'ancienne section
      const eventsToUpdate = this.events.filter(e => e.projectSection === oldSectionName);
      for (const event of eventsToUpdate) {
        event.projectSection = sectionName;
        updateEvent(event);
      }
    },
    openEditSection(sectionName) {
      this.editSectionName = sectionName;
      this.popupSectionVisible = true;
    },
    deleteSection(sectionName) {
      if(!sectionName || sectionName.trim() === '') {
        return;
      }

      const index = this.project.sections.indexOf(sectionName);
      if (index !== -1) {
        this.project.sections.splice(index, 1);
      }

      // Met à jour tous les events qui avaient cette section
      const eventsToUpdate = this.events.filter(e => e.projectSection === sectionName);
      for (const event of eventsToUpdate) {
        event.projectSection = '';
        updateEvent(event);
      }
    },
    handleDeleteEvent(event) {
      // Logique pour supprimer l'event
      const index = this.events.findIndex(e => e.id === event.id);
      if (index !== -1) {
        this.events.splice(index, 1);
      }
      deleteEvent(event);
    },
    handleAddEvent(section) {
      const sectionEvents = this.events.filter(e => e.projectSection === section);
      const newEvent = {
        ...this.emptyEvent,
        projectSection: section || '',
        projectId: this.project.id,
        clientId: this.project.clientId || '',
        order: sectionEvents.length // dernier de la section
      };
      this.popupEvent = newEvent;
      this.popupEventVisible = true;
    },
    moveSectionUp(section) {
      const idx = this.project.sections.indexOf(section);
      if (idx > 0) {
        const tmp = this.project.sections[idx - 1];
        this.project.sections[idx - 1] = section;
        this.project.sections[idx] = tmp;
      }
    },
    moveSectionDown(section) {
      const idx = this.project.sections.indexOf(section);
      if (idx !== -1 && idx < this.project.sections.length - 1) {
        const tmp = this.project.sections[idx + 1];
        this.project.sections[idx + 1] = section;
        this.project.sections[idx] = tmp;
      }
    },
    async moveEventUp(event, sectionEvents, sectionName) {
      const idx = sectionEvents.indexOf(event);
      if (idx > 0) {
        // Échange les ordres
        const prevEvent = sectionEvents[idx - 1];
        const tmpOrder = event.order;
        event.order = prevEvent.order;
        prevEvent.order = tmpOrder;

        // Trie et met à jour tous les orders de la section pour éviter les doublons
        await this.persistSectionOrder(sectionEvents, sectionName);
      }
    },
    async moveEventDown(event, sectionEvents, sectionName) {
      const idx = sectionEvents.indexOf(event);
      if (idx !== -1 && idx < sectionEvents.length - 1) {
        const nextEvent = sectionEvents[idx + 1];
        const tmpOrder = event.order;
        event.order = nextEvent.order;
        nextEvent.order = tmpOrder;

        await this.persistSectionOrder(sectionEvents, sectionName);
      }
    },
    async persistSectionOrder(sectionEvents, sectionName) {
      // Trie par order, puis réassigne les orders pour éviter les trous
      sectionEvents
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .forEach((ev, i) => {
          ev.order = i;
          updateEvent(ev); // Appel Firestore
        });
    },
  },
  setup() {
    // On utilise reactive pour que les events soient réactifs
    const events = reactive([]);
    provide('events', events);

    const perm = inject('userPerm');

    return { events, perm };
  },
  async mounted(){
    const fetchedEvents = await fetchEventsByProjectId(this.project.id);
    // Vider puis remplir l'array pour garder la même référence
    this.events.splice(0, this.events.length, ...fetchedEvents);
    this.ressources = await fetchRessources();
    console.log("Fetched events:", this.events);
    if (!Array.isArray(this.project.sections)) {
      this.project.sections = ["Avant", "Pendant", "Après"];
    }
  },
};
</script>
  
<style scoped>
table {
    border-collapse: separate; /* Nécessaire pour appliquer un border-radius si besoin */
    border-spacing: 0;
}

tbody tr:hover {
    background-color: #ffe0e0 !important; /* ou la couleur que vous souhaitez pour le hover */
}
</style>